import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cartAPI, orderAPI } from '../services/api';
import Navigation from '../components/layout/Navigation';
import { Trash2, Plus, Minus } from 'lucide-react';

const Cart = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await cartAPI.get();
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, newQuantity) => {
    try {
      await cartAPI.update({ product_id: productId, quantity: newQuantity });
      await fetchCart();
    } catch (error) {
      alert('Failed to update cart');
    }
  };

  const removeItem = async (productId) => {
    await updateQuantity(productId, 0);
  };

  const proceedToCheckout = async () => {
    if (cart.items.length === 0) return;
    setProcessing(true);
    try {
      const response = await orderAPI.create({});
      navigate('/checkout', { state: { orderId: response.data.order_id } });
    } catch (error) {
      alert('Failed to create order');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-brand-bg pt-24 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-600"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-brand-bg pt-24 px-6 pb-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-outfit font-light tracking-tight text-white mb-8">Shopping Cart</h1>

          {cart.items.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-zinc-400 mb-4">Your cart is empty</p>
              <a href="/shop" className="inline-block px-6 py-3 bg-violet-600 text-white rounded-md font-medium hover:bg-violet-500 transition-all">
                Start Shopping
              </a>
            </div>
          ) : (
            <div className="space-y-6">
              {cart.items.map((item) => (
                <div key={item.product_id} className="bg-brand-surface border border-brand-border rounded-xl p-6 flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-outfit font-semibold text-white mb-1">{item.product_name}</h3>
                    <p className="text-violet-600 font-bold">{item.price}€</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2 bg-zinc-900 rounded-md">
                      <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="p-2 text-zinc-400 hover:text-white">
                        <Minus size={16} />
                      </button>
                      <span className="text-white font-medium w-8 text-center">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="p-2 text-zinc-400 hover:text-white">
                        <Plus size={16} />
                      </button>
                    </div>
                    <div className="text-white font-bold w-20 text-right">{item.subtotal.toFixed(2)}€</div>
                    <button onClick={() => removeItem(item.product_id)} className="p-2 text-red-400 hover:text-red-300">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}

              <div className="bg-brand-surface border border-brand-border rounded-xl p-6">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-xl font-outfit font-semibold text-white">Total</span>
                  <span className="text-3xl font-bold text-violet-600">{cart.total.toFixed(2)}€</span>
                </div>
                <button
                  onClick={proceedToCheckout}
                  disabled={processing}
                  className="w-full px-6 py-4 bg-violet-600 text-white rounded-md font-medium hover:bg-violet-500 transition-all glow-violet disabled:opacity-50"
                >
                  {processing ? 'Processing...' : 'Proceed to Checkout'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Cart;